import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { StateHistoryEntry, StateHistoryTriggerSource } from '@/types/stateHistory';

/**
 * Records a state change to the state_history table.
 * This is a standalone function so it can be called from any context.
 */
export async function recordStateChange(params: {
  teamId: string;
  tagId: string;
  fromStateId: string | null;
  toStateId: string;
  triggerSource: StateHistoryTriggerSource;
  changedByMemberId?: string | null;
  changedByDeviceId?: string | null;
  triggerReference?: string | null;
}) {
  const { error } = await (supabase
    .from('state_history' as any)
    .insert({
      team_id: params.teamId,
      tag_id: params.tagId,
      from_state_id: params.fromStateId,
      to_state_id: params.toStateId,
      trigger_source: params.triggerSource,
      changed_by_member_id: params.changedByMemberId || null,
      changed_by_device_id: params.changedByDeviceId || null,
      trigger_reference: params.triggerReference || null,
    }) as any);

  if (error) {
    console.error('Error recording state history:', error);
  }
}

function mapRow(row: any): StateHistoryEntry {
  return {
    id: row.id,
    teamId: row.team_id,
    tagId: row.tag_id,
    fromStateId: row.from_state_id,
    toStateId: row.to_state_id,
    changedByMemberId: row.changed_by_member_id,
    changedByDeviceId: row.changed_by_device_id,
    triggerSource: row.trigger_source,
    triggerReference: row.trigger_reference,
    createdAt: new Date(row.created_at),
  };
}

/**
 * Hook to fetch state history for a specific tag.
 */
export function useStateHistory(teamId: string | null, tagId: string | null) {
  const [history, setHistory] = useState<StateHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async (limit = 50) => {
    if (!teamId || !tagId) {
      setHistory([]);
      return;
    }

    setLoading(true);
    const { data, error } = await (supabase
      .from('state_history' as any)
      .select('*')
      .eq('team_id', teamId)
      .eq('tag_id', tagId)
      .order('created_at', { ascending: false })
      .limit(limit) as any);

    if (error) {
      console.error('Error fetching state history:', error);
    } else {
      setHistory((data as any[]).map(mapRow));
    }
    setLoading(false);
  }, [teamId, tagId]);

  return { history, loading, fetchHistory };
}
